import { createTheme } from '@mui/material/styles';

// Public website theme — v5 brand (deep green · teal · yellow · mint).
//
// This drives every MUI component on the website: buttons, cards, inputs,
// chips, typography. The six portals do NOT use it — they are pinned to
// theme/portalTheme.js at the route in routes/AppRoutes.js — so changes
// here cannot reach them.
//
// The one exception is MuiCssBaseline, which renders once at App level and
// writes to <body>. Anything put on `body` here would reach the portals as
// well, so the website's own ground and typeface are set on `.tl-site`
// instead, the class MainLayout and SiteScope put on the site's roots.
export const colors = {
  // greens
  g9: '#0B2B27',
  g8: '#12362F',
  g7: '#16413A',
  g6: '#23463F',
  // teals
  teal: '#23A991',
  tealDark: '#157A69',
  tealGlow: '#5FE0C6',
  teal50: '#E6FAF5',
  // brand yellow — the primary action colour
  yellow: '#FCEB00',
  yellowDark: '#F5E100',
  gold: '#E8B500',
  // surfaces + lines
  mint: '#EEF6F3',
  mint2: '#E4F1ED',
  line: '#D5E5E0',
  line2: '#C7DDD7',
  muted: '#4A605B',
  muted2: '#5A706B',
  soft: '#B6CFC8',
  white: '#FFFFFF',
  // semantics
  ok: '#147A3C',
  ok50: '#E6F5EC',
  warn: '#9A5200',
  warn50: '#FFF3E0',
  bad: '#B42318',
  bad50: '#FDECEA',
  info: '#1D4ED8',
  info50: '#EAF0FE',
};

const DISPLAY = "'Bricolage Grotesque', ui-sans-serif, system-ui, sans-serif";
const UI = "'Manrope', ui-sans-serif, system-ui, sans-serif";
const EASE = 'cubic-bezier(.2,.7,.2,1)';

const theme = createTheme({
  palette: {
    primary: {
      main: colors.tealDark,
      dark: colors.g9,
      light: colors.teal,
      contrastText: '#fff',
    },
    // Brand yellow. Carries dark ink, never white — #FCEB00 with white text
    // is about 1.1:1 and unreadable.
    secondary: {
      main: colors.yellow,
      dark: colors.yellowDark,
      light: '#FFF7B3',
      contrastText: colors.g9,
    },
    success: { main: colors.ok, light: colors.ok50 },
    error: { main: colors.bad, light: colors.bad50 },
    warning: { main: colors.warn, light: colors.warn50 },
    info: { main: colors.info, light: colors.info50 },
    background: { default: colors.mint, paper: '#fff' },
    text: { primary: colors.g9, secondary: colors.muted },
    divider: colors.line,
  },

  typography: {
    fontFamily: UI,
    h1: {
      fontFamily: DISPLAY,
      fontWeight: 700,
      lineHeight: 0.95,
      letterSpacing: '-.04em',
      fontSize: 'clamp(44px, 6.4vw, 88px)',
    },
    h2: {
      fontFamily: DISPLAY,
      fontWeight: 700,
      lineHeight: 1.02,
      letterSpacing: '-.035em',
      fontSize: 'clamp(32px, 4.4vw, 56px)',
    },
    h3: {
      fontFamily: DISPLAY,
      fontWeight: 700,
      lineHeight: 1.08,
      letterSpacing: '-.03em',
      fontSize: 'clamp(24px, 2.6vw, 36px)',
    },
    h4: { fontFamily: UI, fontSize: 'clamp(18px, 1.7vw, 22px)', fontWeight: 800, letterSpacing: '-.01em', lineHeight: 1.25 },
    h5: { fontFamily: UI, fontSize: '17px', fontWeight: 800, lineHeight: 1.3 },
    body1: { fontFamily: UI, fontSize: '16px', lineHeight: 1.6, color: colors.muted },
    body2: { fontFamily: UI, fontSize: '14px', lineHeight: 1.55, color: colors.muted2 },
    button: { fontFamily: UI, fontWeight: 800, textTransform: 'none', letterSpacing: 0 },
    caption: { fontFamily: UI, fontSize: '12.5px', fontWeight: 800, letterSpacing: '.02em', textTransform: 'none', color: colors.tealDark },
  },

  shape: { borderRadius: 20 },

  components: {
    MuiCssBaseline: {
      styleOverrides: `
        @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Cormorant+Garamond:ital,wght@0,400;0,500;0,600;0,700;1,400;1,600&family=DM+Sans:wght@300;400;500;600;700&family=Outfit:wght@300;400;500;600;700;800&family=JetBrains+Mono:wght@400;500&family=Playfair+Display:wght@400;700&family=Space+Mono:wght@400;700&display=swap');

        html { scroll-behavior: smooth; }
        * { box-sizing: border-box; }
        a { text-decoration: none; color: inherit; }

        /* CssBaseline copies typography.fontFamily onto <body>, and there is
           exactly one CssBaseline for the whole app — so the website's
           Manrope would land on the portals too. These rules are emitted
           after the baseline's own, so they win and hold <body> on the
           typeface the portals were built in. The website overrides it
           below on .tl-site. */
        body {
          font-family: 'Outfit', sans-serif;
          background: ${colors.mint};
        }

        /* Website ground and typeface. Deliberately NOT on <body>: this
           stylesheet is emitted once for the whole app, and the portals
           render from the same document. Scoping to .tl-site keeps them
           on their own typeface. */
        .tl-site {
          background: ${colors.mint};
          color: ${colors.g9};
          font-family: ${UI};
          -webkit-font-smoothing: antialiased;
        }
        /* Every page sits on the mint ground — home and inner alike.
           This used to force inner pages white, carried over from the
           previous reference where home loaded a mint stylesheet and
           inner pages a white one. The current reference does not split
           them: all 30 of its pages declare a mint body, checked page by
           page. White was actively harmful — the design puts white cards
           on the mint ground, so on an inner page every card was
           white-on-white and simply disappeared (About's accreditation
           and leadership cards were invisible). MainLayout still adds
           tl-inner / tl-tinted, but neither paints anything now. */

        .tl-site ::selection { background: ${colors.yellow}; color: ${colors.g9}; }

        .serif { font-family: ${DISPLAY}; }
        .mono { font-family: 'JetBrains Mono', monospace; }
        .display { font-family: ${DISPLAY}; letter-spacing: -0.03em; }

        @keyframes fadeUp { from { opacity: 0; transform: translateY(18px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes pulse { 0%, 100% { transform: scale(1); opacity: 0.5; } 50% { transform: scale(1.6); opacity: 0; } }
        @keyframes blink { 0%, 100% { opacity: 1; } 50% { opacity: 0.2; } }
      `,
    },

    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          borderRadius: 999,
          fontWeight: 800,
          textTransform: 'none',
          boxShadow: 'none',
          minHeight: 44,
          transition: `transform .35s ${EASE}, box-shadow .35s ${EASE}, background-color .25s`,
          '&:hover': { boxShadow: 'none', transform: 'translateY(-2px)' },
          '&:active': { transform: 'scale(.97)' },
        },
        sizeSmall: { padding: '8px 18px', fontSize: '14px', minHeight: 40 },
        sizeMedium: { padding: '11px 22px', fontSize: '15px' },
        sizeLarge: { padding: '15px 28px', fontSize: '16px', minHeight: 48 },
        // The brand's primary action is yellow on dark ink, matching the
        // "Book a test" button in the chrome.
        containedPrimary: {
          backgroundColor: colors.yellow,
          color: colors.g9,
          '&:hover': {
            backgroundColor: colors.yellowDark,
            color: colors.g9,
            boxShadow: '0 14px 30px -12px rgba(232,181,0,.65)',
          },
        },
        containedSecondary: {
          backgroundColor: colors.g9,
          color: '#fff',
          '&:hover': { backgroundColor: colors.g8, color: '#fff', boxShadow: 'var(--tl-sh-2)' },
        },
        outlined: {
          borderWidth: 1.5,
          borderColor: colors.line2,
          color: colors.g9,
          '&:hover': {
            borderWidth: 1.5,
            borderColor: colors.g9,
            color: colors.g9,
            backgroundColor: 'transparent',
          },
        },
        text: {
          color: colors.tealDark,
          '&:hover': { backgroundColor: 'transparent', color: colors.g9 },
        },
      },
    },

    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 24,
          border: 0,
          boxShadow: '0 1px 2px rgba(11,43,39,.06)',
          transition: `transform .45s ${EASE}, box-shadow .45s ${EASE}`,
        },
      },
    },

    MuiPaper: { styleOverrides: { rounded: { borderRadius: 20 } } },

    MuiChip: {
      styleOverrides: {
        root: { fontFamily: UI, fontWeight: 800, fontSize: 12, letterSpacing: 0, borderRadius: 999 },
      },
    },

    MuiTextField: {
      defaultProps: { variant: 'outlined', size: 'medium' },
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 14,
            backgroundColor: '#fff',
            fontFamily: UI,
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: colors.teal },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: colors.tealDark, borderWidth: 1.5 },
          },
          '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.line },
        },
      },
    },

    MuiAppBar: { styleOverrides: { root: { boxShadow: 'none' } } },

    MuiContainer: {
      styleOverrides: { root: { '@media (min-width: 1200px)': { maxWidth: 1320 } } },
    },

    // Stops MUI adding `padding-right` to <body> when a Menu/Select/
    // Autocomplete opens, which visibly shifts the page right.
    MuiPopover: { defaultProps: { disableScrollLock: true } },
    MuiMenu: { defaultProps: { disableScrollLock: true } },
  },
});

export default theme;
